// i18n setup per docs/ui/operator-ui-tds.md §10 (NEX-151).
//
// react-i18next + LanguageDetector with three bundled languages
// (en / es / pt-BR). The per-tenant default language map lives in
// `src/config/tenants.json`. If the visiting operator has no stored
// language preference (localStorage), we fall back to the tenant default;
// otherwise to en.

import i18n, { type i18n as I18nInstance, type InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enCommon from './locales/en/common.json';
import esCommon from './locales/es/common.json';
import ptCommon from './locales/pt-BR/common.json';
import enDisruptions from './locales/en/disruptions.json';
import esDisruptions from './locales/es/disruptions.json';
import enRoster from './locales/en/roster.json';
import esRoster from './locales/es/roster.json';
import enRebooking from './locales/en/rebooking.json';
import esRebooking from './locales/es/rebooking.json';
import enTransport from './locales/en/transport.json';
import esTransport from './locales/es/transport.json';
import enCare from './locales/en/care.json';
import esCare from './locales/es/care.json';
import enTower from './locales/en/tower.json';
import esTower from './locales/es/tower.json';
import enPolicyRules from './locales/en/policyRules.json';
import esPolicyRules from './locales/es/policyRules.json';
import enPolicies from './locales/en/policies.json';
import esPolicies from './locales/es/policies.json';
import enPolicyHelp from './locales/en/policyHelp.json';
import esPolicyHelp from './locales/es/policyHelp.json';
import enCaseDetail from './locales/en/caseDetail.json';
import esCaseDetail from './locales/es/caseDetail.json';
import enCaseWork from './locales/en/caseWork.json';
import esCaseWork from './locales/es/caseWork.json';
import enPnr from './locales/en/pnr.json';
import esPnr from './locales/es/pnr.json';
import enPnrActions from './locales/en/pnrActions.json';
import esPnrActions from './locales/es/pnrActions.json';
import enCases from './locales/en/cases.json';
import esCases from './locales/es/cases.json';
import enNetwork from './locales/en/network.json';
import esNetwork from './locales/es/network.json';
import enAdmin from './locales/en/admin.json';
import esAdmin from './locales/es/admin.json';
import enWhy from './locales/en/why.json';
import esWhy from './locales/es/why.json';
import enReservations from './locales/en/reservations.json';
import esReservations from './locales/es/reservations.json';
import enLcc from './locales/en/lcc.json';
import esLcc from './locales/es/lcc.json';
import enFlightTicket from './locales/en/flightTicket.json';
import esFlightTicket from './locales/es/flightTicket.json';
import enCounters from './locales/en/counters.json';
import esCounters from './locales/es/counters.json';
import enProviderSim from './locales/en/providerSim.json';
import esProviderSim from './locales/es/providerSim.json';
import enDeclare from './locales/en/declare.json';
import esDeclare from './locales/es/declare.json';
import enOperate from './locales/en/operate.json';
import esOperate from './locales/es/operate.json';
import enOperateDelay from './locales/en/operateDelay.json';
import esOperateDelay from './locales/es/operateDelay.json';
import enOperateEscalation from './locales/en/operateEscalation.json';
import esOperateEscalation from './locales/es/operateEscalation.json';
import enOperateCancel from './locales/en/operateCancel.json';
import esOperateCancel from './locales/es/operateCancel.json';
import enOperatePax from './locales/en/operatePax.json';
import esOperatePax from './locales/es/operatePax.json';
import enOperateMeals from './locales/en/operateMeals.json';
import esOperateMeals from './locales/es/operateMeals.json';
import enOperateHistory from './locales/en/operateHistory.json';
import esOperateHistory from './locales/es/operateHistory.json';
import enOperateWork from './locales/en/operateWork.json';
import esOperateWork from './locales/es/operateWork.json';
import enAlerts from './locales/en/alerts.json';
import esAlerts from './locales/es/alerts.json';
import enPoliciesUx from './locales/en/policiesUx.json';
import esPoliciesUx from './locales/es/policiesUx.json';
import enPoliciesMap from './locales/en/policiesMap.json';
import esPoliciesMap from './locales/es/policiesMap.json';
import enPoliciesSimulator from './locales/en/policiesSimulator.json';
import esPoliciesSimulator from './locales/es/policiesSimulator.json';
import enPoliciesCompare from './locales/en/policiesCompare.json';
import esPoliciesCompare from './locales/es/policiesCompare.json';
import enPoliciesWizard from './locales/en/policiesWizard.json';
import esPoliciesWizard from './locales/es/policiesWizard.json';
import enPoliciesActivation from './locales/en/policiesActivation.json';
import esPoliciesActivation from './locales/es/policiesActivation.json';
import enBilling from './locales/en/billing.json';
import esBilling from './locales/es/billing.json';
import enScreensaver from './locales/en/screensaver.json';
import esScreensaver from './locales/es/screensaver.json';
import tenants from '@/config/tenants.json';

const SUPPORTED = ['en', 'es', 'pt-BR'] as const;
type SupportedLng = (typeof SUPPORTED)[number];

// English-only was a product decision on 2026-08-24 (AVAILABLE = ['en']). It is
// reversed here, deliberately: the product requirement is full en + es coverage,
// so Spanish is AVAILABLE again and every console surface carries a Spanish
// translation with enforced key parity (see i18n.test.ts).
//
// pt-BR stays BUNDLED but NOT available: its coverage stops at the `common`
// namespace, so offering it would drop an operator into a console that is
// Portuguese in the chrome and English everywhere else. Re-enabling it means
// translating the 17 feature namespaces below and adding 'pt-BR' here.
const AVAILABLE: readonly SupportedLng[] = ['en', 'es'];

// AVAILABLE is what the console OFFERS (the topbar toggle cycles it, and it is
// what auto-detection is allowed to land on). i18next's `supportedLngs` is
// wider on purpose — see the pt-BR note on `createI18nOptions` below.

/**
 * Every namespace we bundle. `common` is the default namespace (app chrome,
 * shared vocabulary); the rest are feature-scoped so that lanes editing
 * different surfaces do not collide in one giant file. A namespace listed here
 * MUST have an `en` and an `es` file under `locales/<lng>/<ns>.json` — the
 * parity test walks this list.
 */
export const NAMESPACES = [
  'common',
  'disruptions',
  'roster',
  'rebooking',
  'transport',
  'care',
  'tower',
  'policyRules',
  'policies',
  'policyHelp',
  'caseDetail',
  'caseWork',
  'pnr',
  'pnrActions',
  'cases',
  'network',
  'admin',
  'why',
  'lcc',
  'flightTicket',
  'reservations',
  'counters',
  'providerSim',
  'declare',
  'operate',
  'operateDelay',
  'operateEscalation',
  'operateCancel',
  'operatePax',
  'operateMeals',
  'operateHistory',
  'operateWork',
  'alerts',
  'policiesUx',
  'policiesMap',
  'policiesSimulator',
  'policiesCompare',
  'policiesWizard',
  'policiesActivation',
  'billing',
  'screensaver',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

const TENANT = import.meta.env.VITE_TENANT ?? '';
const tenantDefault =
  (tenants as Record<string, { defaultLanguage?: string }>)[TENANT]?.defaultLanguage;
const fallback: SupportedLng = (
  AVAILABLE as readonly string[]
).includes(tenantDefault ?? '')
  ? (tenantDefault as SupportedLng)
  : 'en';

/**
 * The live init options, factored out so the parity/resolution test can spin a
 * second instance with a different tenant fallback and assert the REAL config
 * (detection order included) rather than a copy of it.
 *
 * `supportedLngs` carries pt-BR even though pt-BR is not AVAILABLE: two
 * surfaces ship their own complete trilingual bundles that read the console's
 * active language (`lib/manifest-imports/copy.ts`, `lib/manifest-formats`), so
 * an EXPLICIT `changeLanguage('pt-BR')` must still stick for them. What must
 * never happen is *landing* there by accident — a Brazilian browser, or a
 * localStorage value left over from when pt-BR was still offered. The guard
 * right after `init()` handles that: detection may only settle on AVAILABLE.
 */
export function createI18nOptions(fallbackLng: SupportedLng): InitOptions {
  return {
    fallbackLng,
    supportedLngs: [...SUPPORTED],
    ns: [...NAMESPACES],
    debug: false,
    resources: {
      en: {
        common: enCommon,
        disruptions: enDisruptions,
        roster: enRoster,
        rebooking: enRebooking,
        transport: enTransport,
        care: enCare,
        tower: enTower,
        policyRules: enPolicyRules,
        policies: enPolicies,
        policyHelp: enPolicyHelp,
        caseDetail: enCaseDetail,
        caseWork: enCaseWork,
        pnr: enPnr,
        pnrActions: enPnrActions,
        cases: enCases,
        network: enNetwork,
        admin: enAdmin,
        why: enWhy,
        lcc: enLcc,
        flightTicket: enFlightTicket,
        counters: enCounters,
        reservations: enReservations,
        providerSim: enProviderSim,
        declare: enDeclare,
        operate: enOperate,
        operateDelay: enOperateDelay,
        operateEscalation: enOperateEscalation,
        operateCancel: enOperateCancel,
        operatePax: enOperatePax,
        operateMeals: enOperateMeals,
        operateHistory: enOperateHistory,
        operateWork: enOperateWork,
        alerts: enAlerts,
        policiesUx: enPoliciesUx,
        policiesMap: enPoliciesMap,
        policiesSimulator: enPoliciesSimulator,
        policiesCompare: enPoliciesCompare,
        policiesWizard: enPoliciesWizard,
        policiesActivation: enPoliciesActivation,
        billing: enBilling,
        screensaver: enScreensaver,
      },
      es: {
        common: esCommon,
        disruptions: esDisruptions,
        roster: esRoster,
        rebooking: esRebooking,
        transport: esTransport,
        care: esCare,
        tower: esTower,
        policyRules: esPolicyRules,
        policies: esPolicies,
        policyHelp: esPolicyHelp,
        caseDetail: esCaseDetail,
        caseWork: esCaseWork,
        pnr: esPnr,
        pnrActions: esPnrActions,
        cases: esCases,
        network: esNetwork,
        admin: esAdmin,
        why: esWhy,
        lcc: esLcc,
        flightTicket: esFlightTicket,
        counters: esCounters,
        providerSim: esProviderSim,
        reservations: esReservations,
        declare: esDeclare,
        operate: esOperate,
        operateDelay: esOperateDelay,
        operateEscalation: esOperateEscalation,
        operateCancel: esOperateCancel,
        operatePax: esOperatePax,
        operateMeals: esOperateMeals,
        operateHistory: esOperateHistory,
        operateWork: esOperateWork,
        alerts: esAlerts,
        policiesUx: esPoliciesUx,
        policiesMap: esPoliciesMap,
        policiesSimulator: esPoliciesSimulator,
        policiesCompare: esPoliciesCompare,
        policiesWizard: esPoliciesWizard,
        policiesActivation: esPoliciesActivation,
        billing: esBilling,
        screensaver: esScreensaver,
      },
      // pt-BR ships `common` only — see the AVAILABLE comment above.
      'pt-BR': { common: ptCommon },
    },
    defaultNS: 'common',
    detection: {
      // Stored preference wins; otherwise pick the browser language; otherwise
      // the tenant default we set as fallbackLng. Note the ORDER: an English
      // browser on a LATAM/SKY build (tenant default `es`) stays ENGLISH,
      // because navigator resolves before fallbackLng is ever consulted.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'operator-ui:lang',
    },
    interpolation: {
      escapeValue: false,
    },
  };
}

/**
 * Detection may only settle on a language we actually OFFER. A pt-BR browser —
 * or a stale `operator-ui:lang` from before the English-only decision — would
 * otherwise open a console that is Portuguese in the chrome and English in
 * every feature. Re-selecting the fallback also rewrites the cached preference,
 * so the stale value does not come back on the next load.
 *
 * Only the language DETECTED at boot is corrected; an explicit in-session
 * `changeLanguage` is left alone (see the pt-BR note on `createI18nOptions`).
 */
export async function enforceAvailableLanguage(
  instance: I18nInstance,
  fallbackLng: SupportedLng,
): Promise<void> {
  const resolved = instance.resolvedLanguage;
  if (resolved && !(AVAILABLE as readonly string[]).includes(resolved)) {
    await instance.changeLanguage(fallbackLng);
  }
}

await i18n.use(LanguageDetector).use(initReactI18next).init(createI18nOptions(fallback));
await enforceAvailableLanguage(i18n, fallback);

export { i18n };
export type { SupportedLng };
export const SUPPORTED_LANGUAGES = AVAILABLE;
/** Every language with a bundled locale file — parity is enforced across these. */
export const BUNDLED_LANGUAGES = SUPPORTED;
/** The tenant-derived fallback this build initialised with (test/diagnostics). */
export const FALLBACK_LANGUAGE = fallback;
